import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Button,
  IconButton,
  Radio,
  Typography,
  Paper,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import CloseIcon from "@material-ui/icons/Close";
import DateFnsUtils from "@date-io/date-fns";
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker,
} from "@material-ui/pickers";
import SearchIcon from "@material-ui/icons/Search";
import moment from "moment";

/** ------------------------------------------------------------------
 *  Styles
 *  ------------------------------------------------------------------ */
const useStyles = makeStyles((theme) => ({
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    borderColor: "#ec5269",
    color: "#ec5269",
    background: "#fff",
    marginBottom: 10,
    height: 50,
  },
  sectionTitle: {
    position: "relative",
    paddingTop: 18,
    paddingBottom: 18,
    color: "#7c7c7c",
    fontWeight: 600,
    fontSize: 18,
    margin: "8px 0 12px",
  },
  close: { position: "absolute", right: 8, top: 8 },
  gutter: { marginBottom: 12 },
  searchBar: {
    border: "1px solid #e5e5e5",
    borderRadius: 6,
    padding: 12,
    background: "#fff",
  },
  listPaper: { paddingBottom: 8 },
  headCell: {
    color: "#7c7c7c",
    fontWeight: 600,
    fontSize: 14,
    paddingLeft: 24,
    paddingRight: 24,
    whiteSpace: "nowrap",
  },
  bodyCell: {
    fontSize: 14,
    paddingLeft: 24,
    paddingRight: 24,
  },
  radioCell: { width: 48, paddingLeft: 16, paddingRight: 16 },
  rowClickable: {
    "&:hover": { backgroundColor: "#fafafa" },
    cursor: "pointer",
  },
  resultHeaderRow: {
    paddingTop: 4,
    paddingBottom: 4,
  },
  resultHeaderInner: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 8,
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: 12,
    padding: 12,
  },
  buttonStyle: {
    background: "#EC5269",
    color: "white",
    textTransform: "none",
    borderRadius: 10,
    width: 120,
    margin: "1%",
  },
  formControl: {
    minWidth: 180,
  },
  dialogBodySection: {
    backgroundColor: "#F4F6F8",
    padding: theme.spacing(3),
  },
  paginationActions: {
    "& .MuiIconButton-root": {
      color: "#EC5269",
    },
    "& .MuiIconButton-root:hover": {
      backgroundColor: "rgba(236,82,105,0.08)",
    },
  },
}));

/** ------------------------------------------------------------------
 *  Helpers
 *  ------------------------------------------------------------------ */
const FIELDS = [
  { label: "Competition Name", value: "competition_name" },
  { label: "Competition Id", value: "usef_competition_id" },
  { label: "Start Date", value: "start_date" },
];

const DEFAULT_PAGE_SIZE = 5;

function formatIsoToDisplay(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${m}/${d}/${y}`;
}

function validateDisplayDate(display) {
  if (!display) return "";
  const m = moment(display, "MM/DD/YYYY", true);
  if (!m.isValid()) {
    return "";
  }
  return m.format("YYYY-MM-DD");
}

class CustomDateFnsUtils extends DateFnsUtils {
  getWeekdays() {
    return ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  }
}

/** ------------------------------------------------------------------
 *  Component
 *  ------------------------------------------------------------------ */
export default function CompetitionSearchDialog({
  open,
  onClose,
  onSelect,
  Api = { search: async () => ({ rows: [], total: 0 }) },
  selectedCompId,
}) {
  const classes = useStyles();

  // ------------------------- Local state ------------------------- //
  const [searchBy, setSearchBy] = React.useState("competition_name");
  const [inputs, setInputs] = React.useState({
    compName: "",
    compId: "",
    startDateDisplay: "",
  });

  const [dateError, setDateError] = React.useState(false);

  const [rows, setRows] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [selectedIdx, setSelectedIdx] = React.useState(null);

  const [pageIndex, setPageIndex] = React.useState(0); // zero-based
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_PAGE_SIZE);

  const [lastQuery, setLastQuery] = React.useState("");
  const [lastSearchBy, setLastSearchBy] = React.useState("");

  const [searchLoading, setSearchLoading] = React.useState(false);
  const [tableLoading, setTableLoading] = React.useState(false);

  // keep a snapshot of the criteria used for the current result set
  const criteriaRef = React.useRef({
    searchBy: "competition_name",
    compName: "",
    compId: "",
    startDateIso: "",
  });

  const dateInputRef = React.useRef(null);
  const mountedRef = React.useRef(false);
  const sessionRef = React.useRef(false);

  React.useEffect(() => {
    mountedRef.current = true;
    return () => void (mountedRef.current = false);
  }, []);

  React.useEffect(() => {
    if (open) {
      sessionRef.current = true;
      return () => void (sessionRef.current = false);
    }
  }, [open]);

  // ------------------------- Data fetching ------------------------- //
  const fetchPage = React.useCallback(
    async (pageOverride, sizeOverride) => {
      const page = (pageOverride ?? pageIndex) + 1; // API is 1-based
      const size = sizeOverride ?? rowsPerPage;
      const {
        searchBy: mode,
        compName,
        compId,
        startDateIso,
      } = criteriaRef.current;

      const payload = {
        compName: mode === "competition_name" ? compName.trim() : "",
        compId: mode === "usef_competition_id" ? compId.trim() : "",
        startDate: mode === "start_date" ? startDateIso : "",
        page,
        size,
        selectedCompId,
      };

      setTableLoading(true);
      try {
        const res = await Api.search(payload);
        if (!mountedRef.current || !sessionRef.current) return;

        const nextRows = Array.isArray(res?.rows)
          ? res.rows
          : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res?.data)
          ? res.data
          : [];

        const nextTotal =
          typeof res?.total === "number"
            ? res.total
            : typeof res?.data?.total === "number"
            ? res.data.total
            : nextRows.length;

        setRows(nextRows);
        setTotal(nextTotal);
        setSelectedIdx(null);
      } finally {
        setTableLoading(false);
      }
    },
    [Api, pageIndex, rowsPerPage, selectedCompId]
  );

  const runSearch = React.useCallback(async () => {
    if (searchBy === "competition_name" && !inputs.compName.trim()) {
      setRows([]);
      setSelectedIdx(null);
      setLastQuery("");
      setTotal(0);
      setPageIndex(0);
      return;
    }

    if (searchBy === "usef_competition_id" && !inputs.compId.trim()) {
      setRows([]);
      setSelectedIdx(null);
      setLastQuery("");
      setTotal(0);
      setPageIndex(0);
      return;
    }
    let startDateIso = "";

    if (searchBy === "start_date") {
      const raw = (
        dateInputRef.current?.value ??
        inputs.startDateDisplay ??
        ""
      ).trim();
      if (!raw) {
        setDateError(false);
        setRows([]);
        setSelectedIdx(null);
        setLastQuery("");
        setTotal(0);
        setPageIndex(0);
        return;
      }
      const normalized = validateDisplayDate(raw);
      if (raw && !normalized) {
        setDateError(true);
        setLastQuery("");
        setRows([]);
        setSelectedIdx(null);
        setTotal(0);
        setPageIndex(0);
        return;
      }
      setDateError(false);
      startDateIso = normalized;
      if (raw !== inputs.startDateDisplay) {
        setInputs((s) => ({ ...s, startDateDisplay: raw }));
      }
    }

    criteriaRef.current = {
      searchBy,
      compName: inputs.compName,
      compId: inputs.compId,
      startDateIso,
    };

    setLastSearchBy(searchBy);
    setLastQuery(
      searchBy === "competition_name"
        ? inputs.compName.trim()
        : searchBy === "usef_competition_id"
        ? inputs.compId.trim()
        : inputs.startDateDisplay.trim()
    );

    setPageIndex(0);
    setSearchLoading(true);
    try {
      await fetchPage(0, rowsPerPage);
    } finally {
      setSearchLoading(false);
    }
  }, [
    fetchPage,
    inputs.compId,
    inputs.compName,
    inputs.startDateDisplay,
    rowsPerPage,
    searchBy,
  ]);

  React.useEffect(() => {
    if (!open || !lastQuery) return;
    fetchPage(pageIndex);
  }, [open, lastQuery, pageIndex, fetchPage]);

  React.useEffect(() => {
    if (open) return;
    resetAll();
  }, [open]);

  // ------------------------- Handlers ------------------------- //
  const resetAll = React.useCallback(() => {
    setSearchBy("competition_name");
    setInputs({ compName: "", compId: "", startDateDisplay: "" });
    setDateError(false);
    setRows([]);
    setSelectedIdx(null);
    setLastQuery("");
    setLastSearchBy("");
    setPageIndex(0);
    setTotal(0);
    setRowsPerPage(DEFAULT_PAGE_SIZE);
  }, []);

  const handleClearResults = React.useCallback(() => {
    setRows([]);
    setSelectedIdx(null);
    setLastQuery("");
    setLastSearchBy("");
    setInputs({ compName: "", compId: "", startDateDisplay: "" });
    setDateError(false);
    setPageIndex(0);
    setTotal(0);
  }, []);

  const handleEnter = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      runSearch();
    }
  };

  const handleDialogClose = () => {
    resetAll();
    onClose();
  };

  // ------------------------- Render ------------------------- //
  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      keepMounted
      maxWidth="md"
      fullWidth
      aria-labelledby="search-comps-title"
    >
      <DialogTitle
        id="search-comps-title"
        className={classes.sectionTitle}
        disableTypography
      >
        <Typography variant="h6" component="h2">
          Search Competitions
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          className={classes.close}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <div className={classes.dialogBodySection}>
          {/* Search bar */}
          <div className={`${classes.searchBar} ${classes.gutter}`}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <SearchIcon
                  fontSize="small"
                  color="action"
                  aria-hidden
                  style={{ opacity: 0.7 }}
                />
              </Grid>

              {/* Field selector */}
              <Grid item xs={12} sm={6} md={4}>
                <FormControl
                  variant="outlined"
                  size="small"
                  fullWidth
                  className={classes.formControl}
                >
                  <Select
                    value={searchBy}
                    onChange={(e) => {
                      const next = e.target.value;
                      setSearchBy(next);
                      // wipe inputs & results when switching modes
                      handleClearResults();
                    }}
                    inputProps={{ "aria-label": "Search by" }}
                  >
                    {FIELDS.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* Competition Name */}
              {searchBy === "competition_name" && (
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    placeholder="Competition Name"
                    value={inputs.compName}
                    onChange={(e) =>
                      setInputs((s) => ({ ...s, compName: e.target.value }))
                    }
                    onKeyDown={handleEnter}
                    variant="outlined"
                    size="small"
                    fullWidth
                    autoFocus
                  />
                </Grid>
              )}

              {/* Competition Id */}
              {searchBy === "usef_competition_id" && (
                <Grid item xs={12} sm={6} md={4}>
                  <TextField
                    placeholder="Competition Id"
                    value={inputs.compId}
                    onChange={(e) =>
                      setInputs((s) => ({
                        ...s,
                        compId: e.target.value.replace(/\D+/g, ""),
                      }))
                    }
                    onKeyDown={handleEnter}
                    variant="outlined"
                    size="small"
                    fullWidth
                    inputProps={{
                      inputMode: "numeric",
                      pattern: "[0-9]*",
                      "aria-label": "Competition Id",
                    }}
                    autoFocus
                  />
                </Grid>
              )}

              {/* Start Date */}
              {searchBy === "start_date" && (
                <Grid item xs={12} sm={6} md={4}>
                  <MuiPickersUtilsProvider utils={CustomDateFnsUtils}>
                    <KeyboardDatePicker
                      disableFuture
                      label={dateError ? "Enter valid start date" : ""}
                      format="MM/dd/yyyy"
                      placeholder="MM/DD/YYYY"
                      value={inputs.startDateDisplay || null}
                      disableMaskedInput
                      KeyboardButtonProps={{
                        tabIndex: -1,
                        style: { color: "#EC5269" },
                      }}
                      onChange={(date) => {
                        if (date instanceof Date && !isNaN(date)) {
                          const mm = String(date.getMonth() + 1).padStart(
                            2,
                            "0"
                          );
                          const dd = String(date.getDate()).padStart(2, "0");
                          const yyyy = date.getFullYear();
                          setInputs((s) => ({
                            ...s,
                            startDateDisplay: `${mm}/${dd}/${yyyy}`,
                          }));
                          setDateError(false);
                          setTimeout(() => dateInputRef.current?.focus(), 0);
                        }
                      }}
                      TextFieldComponent={(textFieldProps) => {
                        const { onKeyDown: pickerOnKeyDown, ...rest } =
                          textFieldProps;
                        return (
                          <TextField
                            {...rest}
                            inputRef={dateInputRef}
                            error={dateError}
                            helperText={undefined}
                            InputLabelProps={{ shrink: true }}
                            variant="outlined"
                            fullWidth
                            size="small"
                            onKeyDown={(e) => {
                              handleEnter(e);
                              if (!e.defaultPrevented && pickerOnKeyDown)
                                pickerOnKeyDown(e);
                            }}
                            autoFocus
                          />
                        );
                      }}
                      disableToolbar={false}
                      clearable
                    />
                  </MuiPickersUtilsProvider>
                </Grid>
              )}

              {/* Search button */}
              <Grid item xs={12} md={2}>
                <Button
                  className={classes.buttonStyle}
                  onClick={runSearch}
                  color="primary"
                  variant="contained"
                  fullWidth
                  disabled={searchLoading}
                  aria-label="Search"
                >
                  {searchLoading ? "Searching…" : "SEARCH"}
                </Button>
              </Grid>
            </Grid>
          </div>

          <Divider
            style={{
              background: "#ec5269ff",
              opacity: 1,
              height: 5,
              marginBottom: 20,
            }}
          />

          {/* Results */}
          <Paper elevation={1} className={classes.listPaper}>
            {lastQuery && (
              <Grid
                container
                alignItems="center"
                className={classes.resultHeaderRow}
              >
                <Grid item xs={12} style={{ marginLeft: 10, marginTop: 5 }}>
                  <div className={classes.resultHeaderInner}>
                    <Typography
                      color="inherit"
                      variant="subtitle1"
                      style={{ display: "flex", flexWrap: "wrap" }}
                    >
                      Search results for&nbsp;
                      <span style={{ fontWeight: 600 }}>‘{lastQuery}’</span>
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleClearResults}
                      aria-label="clear search"
                      style={{ padding: 4, color: "#000" }}
                    >
                      <Tooltip title="Clear search">
                        <CloseIcon fontSize="small" />
                      </Tooltip>
                    </IconButton>
                  </div>
                </Grid>
              </Grid>
            )}

            <TableContainer>
              <Table aria-label="Competition results">
                <TableHead>
                  <TableRow>
                    <TableCell className={classes.headCell} />
                    <TableCell className={classes.headCell}>Year</TableCell>
                    <TableCell className={classes.headCell}>Comp ID</TableCell>
                    <TableCell className={classes.headCell}>
                      Competition Name
                    </TableCell>
                    <TableCell className={classes.headCell}>
                      Competition Dates
                    </TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {rows.map((r, idx) => (
                    <TableRow
                      tabIndex={-1}
                      key={`${r.usef_competition_id || idx}-${
                        r.comp_year || ""
                      }`}
                      className={classes.rowClickable}
                      hover
                      selected={selectedIdx === idx}
                      onClick={() => setSelectedIdx(idx)}
                    >
                      <TableCell
                        className={classes.radioCell}
                        padding="checkbox"
                      >
                        <Radio
                          color="primary"
                          checked={selectedIdx === idx}
                          onChange={() => setSelectedIdx(idx)}
                        />
                      </TableCell>

                      <TableCell align="left" className={classes.bodyCell}>
                        {r.comp_year}
                      </TableCell>

                      <TableCell align="left" className={classes.bodyCell}>
                        {r.usef_competition_id}
                      </TableCell>

                      <TableCell align="left" className={classes.bodyCell}>
                        {r.competition_name}
                      </TableCell>

                      <TableCell align="left" className={classes.bodyCell}>
                        {r.start_date ? formatIsoToDisplay(r.start_date) : ""}
                        {r.start_date && r.end_date ? " - " : ""}
                        {r.end_date ? formatIsoToDisplay(r.end_date) : ""}
                      </TableCell>
                    </TableRow>
                  ))}

                  {/* Optional inline loader row */}
                  {tableLoading && rows.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className={classes.bodyCell}>
                        <Typography variant="body2">Loading…</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              classes={{ actions: classes.paginationActions }}
              component="div"
              count={total}
              page={pageIndex}
              onChangePage={(e, newPage) => setPageIndex(newPage)}
              rowsPerPage={rowsPerPage}
              onChangeRowsPerPage={(e) => {
                const next = parseInt(e.target.value, 10) || DEFAULT_PAGE_SIZE;
                setPageIndex(0);
                setRowsPerPage(next);
                // refetch first page with new size if a query is active
                if (lastQuery) fetchPage(0, next);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} of ${count}`
              }
            />
          </Paper>
        </div>
      </DialogContent>

      <DialogActions className={classes.actions}>
        <Button
          className={classes.buttonStyle}
          color="primary"
          variant="contained"
          onClick={() => {
            if (selectedIdx == null) return;
            onSelect(rows[selectedIdx]);
            handleDialogClose();
          }}
          disabled={selectedIdx == null}
        >
          Select
        </Button>
        <Button
          className={classes.buttonStyle}
          color="primary"
          variant="contained"
          onClick={handleDialogClose}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
}
